import { localized, msg } from '@lit/localize';
import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@spectrum-web-components/action-button/sp-action-button.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-menu-hamburger.js';

import './module-switcher';
import '../components/toast-center';
import '../modules/audit';
import '../modules/workflow';
import '../modules/chat';
import '../modules/remoteapp';
import '../modules/settings';
import '../modules/usage';
import '../modules/not-available';
import '../modules/admin';

import { api } from '../api';
import type { FeatureFlags, User } from '../api/types';
import {
  DEFAULT_FEATURE_FLAGS,
  type FeatureFlagKey,
} from '../config/feature-flags';
import type { LocalePreference } from '../i18n/localization';
import { isInternalUser } from '../utils/access';

const FEATURE_FLAG_KEYS: FeatureFlagKey[] = [
  'chat',
  'workflows',
  'remoteapp',
  'audit',
  'agent',
  'settings',
  'usage',
];

function getFeatureFlagsStorageKey(userId: string): string {
  return `kv.featureFlags.${userId}`;
}

function parseCachedFeatureFlags(userId: string): FeatureFlags | null {
  try {
    const key = getFeatureFlagsStorageKey(userId);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const result = { ...DEFAULT_FEATURE_FLAGS };
    for (const k of FEATURE_FLAG_KEYS) {
      if (typeof (parsed as Record<string, unknown>)[k] !== 'boolean')
        return null;
      result[k] = (parsed as Record<string, boolean>)[k];
    }
    return result;
  } catch {
    return null;
  }
}

function persistFeatureFlags(userId: string, flags: FeatureFlags): void {
  try {
    localStorage.setItem(
      getFeatureFlagsStorageKey(userId),
      JSON.stringify(flags)
    );
  } catch (_) {
    /* no-op */
  }
}

/**
 * Main application shell for Klarvos web UI.
 * Hosts top-level module switcher and selected module view.
 */
@customElement('sb-app')
@localized()
export class SbApp extends LitElement {
  static styles = css`
    :host {
      display: block;
      min-width: 0;
      font-family: var(--font);
      color: var(--color-app-text);
    }
    .layout {
      display: block;
      min-width: 0;
      overflow-x: hidden;
      padding-left: var(--kv-module-switcher-width);
      box-sizing: border-box;
    }
    .layout.mobile {
      padding-left: 0;
    }
    .mobile-switcher-fab {
      display: none;
    }
    .mobile-switcher-backdrop {
      display: none;
    }
    .content {
      display: block;
      height: 100%;
      min-height: 0;
      min-width: 0;
      overflow-x: hidden;
      overflow-y: auto;
    }
    .content.chat-active {
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .content.chat-active kv-module-chat {
      flex: 1;
      min-height: 0;
    }
    /* On mobile, same fixed layout for all modules: content fills, only inner module scrolls */
    .content.mobile {
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .content.mobile > * {
      flex: 1;
      min-height: 0;
      min-width: 0;
      overflow-x: auto;
    }
    @media (max-width: 768px) {
      .mobile-switcher-fab {
        position: fixed;
        top: var(--kv-mobile-fab-block-start);
        left: var(--kv-mobile-fab-inline-start);
        z-index: 70;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 4px;
        border-radius: 12px;
        border: 1px solid var(--color-chat-input-border);
        background: var(--color-module-switcher-bg);
      }
      .mobile-switcher-backdrop.open {
        position: fixed;
        inset: 0;
        display: block;
        z-index: 45;
        background: rgba(0, 0, 0, 0.5);
      }
    }
  `;

  @property({ type: Object })
  accessor user!: User;

  @property({ type: String })
  accessor theme: 'light' | 'dark' | 'system' = 'system';

  @property({ type: String })
  accessor localePreference: LocalePreference = 'system';

  @state()
  accessor selectedModule = 'workflows';

  @state()
  accessor isMobileView = false;

  @state()
  accessor mobileModuleSwitcherOpen = false;

  @state()
  accessor featureFlags: FeatureFlags = { ...DEFAULT_FEATURE_FLAGS };

  private mobileQuery =
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 768px)')
      : null;

  private get isAdmin(): boolean {
    return isInternalUser(this.user?.email);
  }

  private get allowedModules(): Set<string> {
    const flags = this.featureFlags;
    const allowed = new Set<string>();
    if (flags.chat) allowed.add('chat');
    if (flags.remoteapp) allowed.add('remoteapp');
    if (flags.workflows) allowed.add('workflows');
    if (flags.agent) allowed.add('agent');
    if (flags.settings) allowed.add('settings');
    if (flags.usage) allowed.add('usage');
    if (this.isAdmin) allowed.add('admin');
    return allowed;
  }

  private onSwitchModule(e: CustomEvent<{ id: string }>) {
    this.selectedModule = e.detail.id;
    if (this.isMobileView) {
      this.mobileModuleSwitcherOpen = false;
    }
  }

  private toggleMobileModuleSwitcher = () => {
    if (!this.isMobileView) return;
    this.mobileModuleSwitcherOpen = !this.mobileModuleSwitcherOpen;
  };

  private closeMobileModuleSwitcher = () => {
    this.mobileModuleSwitcherOpen = false;
  };

  private getModuleFromUrl(): string | undefined {
    const url = new URL(window.location.href);
    const param = url.searchParams.get('module');
    if (!param) return undefined;
    if (param === 'customModels' && this.allowedModules.has('chat')) {
      url.searchParams.set('module', 'chat');
      url.searchParams.set('chatView', 'models');
      window.history.replaceState({}, '', url);
      return 'chat';
    }
    return this.allowedModules.has(param) ? param : undefined;
  }

  private updateUrlModule() {
    const url = new URL(window.location.href);
    url.searchParams.set('module', this.selectedModule);
    if (this.selectedModule !== 'chat') {
      url.searchParams.delete('chatView');
    }
    window.history.replaceState({}, '', url);
  }

  connectedCallback(): void {
    super.connectedCallback();
    if (this.user?.id) {
      const cached = parseCachedFeatureFlags(this.user.id);
      if (cached) this.featureFlags = cached;
    }
    this.syncSelectedModule();
    void this.loadFeatureFlags();
    this.isMobileView = this.mobileQuery?.matches ?? false;
    this.mobileQuery?.addEventListener('change', this.onMobileChange);
  }

  disconnectedCallback(): void {
    this.mobileQuery?.removeEventListener('change', this.onMobileChange);
    super.disconnectedCallback();
  }

  private onMobileChange = (e: MediaQueryListEvent): void => {
    this.isMobileView = e.matches;
    if (!e.matches) {
      this.mobileModuleSwitcherOpen = false;
    }
  };

  protected updated(changed: Map<string, unknown>): void {
    if (changed.has('selectedModule')) {
      try {
        localStorage.setItem('kv.selectedModule', this.selectedModule);
      } catch (_) {
        /* no-op */
      }
      this.updateUrlModule();
    }
    if (changed.has('user') && this.user?.id) {
      const cached = parseCachedFeatureFlags(this.user.id);
      if (cached) this.featureFlags = cached;
      this.syncSelectedModule();
      void this.loadFeatureFlags();
    }
    if (changed.has('featureFlags') || changed.has('user')) {
      this.ensureSelectedModuleAllowed();
    }
  }

  private ensureSelectedModuleAllowed() {
    if (!this.allowedModules.has(this.selectedModule)) {
      this.selectedModule = this.getDefaultModule();
    }
  }

  private syncSelectedModule() {
    const fromUrl = this.getModuleFromUrl();
    if (fromUrl) {
      this.selectedModule = fromUrl;
      return;
    }
    try {
      const saved = localStorage.getItem('kv.selectedModule');
      if (saved === 'customModels' && this.allowedModules.has('chat')) {
        this.selectedModule = 'chat';
        const url = new URL(window.location.href);
        url.searchParams.set('module', 'chat');
        url.searchParams.set('chatView', 'models');
        window.history.replaceState({}, '', url);
        return;
      }
      if (saved && this.allowedModules.has(saved)) {
        this.selectedModule = saved;
        return;
      }
    } catch (_) {
      /* no-op */
    }
    this.selectedModule = this.getDefaultModule();
  }

  private getDefaultModule(): string {
    const order = [
      'chat',
      'workflows',
      'remoteapp',
      'agent',
      'settings',
      'usage',
      'admin',
    ];
    return order.find((module) => this.allowedModules.has(module)) ?? 'chat';
  }

  private async loadFeatureFlags() {
    if (!this.user?.id) return;
    const userId = this.user.id;
    try {
      const res = await api.featureFlags.getMyFlags();
      const flags = res.flags ?? { ...DEFAULT_FEATURE_FLAGS };
      persistFeatureFlags(userId, flags);
      this.featureFlags = flags;
      this.syncSelectedModule();
    } catch (err) {
      console.error('Failed to load feature flags', err);
      this.featureFlags = { ...DEFAULT_FEATURE_FLAGS };
    }
  }

  private renderSelectedModule() {
    const module = this.getResolvedModule();
    switch (module) {
      case 'chat':
        return html`<kv-module-chat
          .mobileSidebarOpen=${this.isMobileView && this.mobileModuleSwitcherOpen}
        ></kv-module-chat>`;
      case 'remoteapp':
        return html`<kv-module-remoteapp></kv-module-remoteapp>`;
      case 'workflows':
        return html`<kv-module-workflow
          .user=${this.user}
        ></kv-module-workflow>`;
      case 'audit':
        return html`<kv-module-audit></kv-module-audit>`;
      case 'settings':
        return html`<kv-module-settings></kv-module-settings>`;
      case 'usage':
        return html`<kv-module-usage></kv-module-usage>`;
      case 'admin':
        return html`<kv-module-admin></kv-module-admin>`;
      default:
        return html`<kv-module-not-available></kv-module-not-available>`;
    }
  }

  private getResolvedModule(): string {
    return this.allowedModules.has(this.selectedModule)
      ? this.selectedModule
      : this.getDefaultModule();
  }

  render() {
    const showModuleSwitcher =
      !this.isMobileView || this.mobileModuleSwitcherOpen;

    return html`
      <div
        class="layout ${this.selectedModule} ${this.isMobileView ? 'mobile' : ''}"
        @switch-module=${this.onSwitchModule}
        @close-mobile-module-switcher=${this.closeMobileModuleSwitcher}
      >
        ${
          this.isMobileView
            ? html`
              ${
                !this.mobileModuleSwitcherOpen
                  ? html`<div class="mobile-switcher-fab">
                      <sp-action-button
                        quiet
                        @click=${this.toggleMobileModuleSwitcher}
                        aria-label=${msg('Open modules')}
                      >
                        <sp-icon-menu-hamburger slot="icon"></sp-icon-menu-hamburger>
                      </sp-action-button>
                    </div>`
                  : null
              }
              <div
                class="mobile-switcher-backdrop ${this.mobileModuleSwitcherOpen ? 'open' : ''}"
                @click=${this.closeMobileModuleSwitcher}
              ></div>
            `
            : null
        }
        ${
          showModuleSwitcher
            ? html`<module-switcher
              .user=${this.user}
              .selected=${this.selectedModule}
              .theme=${this.theme}
              .localePreference=${this.localePreference}
              .featureFlags=${this.featureFlags}
              .isAdmin=${this.isAdmin}
            ></module-switcher>`
            : null
        }
        <div class="content ${this.selectedModule === 'chat' ? 'chat-active' : ''} ${this.isMobileView ? 'mobile' : ''}">
          ${this.renderSelectedModule()}
        </div>
      </div>
      <kv-toast-center></kv-toast-center>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sb-app': SbApp;
  }
}
